import { fallbackRuntimeScene } from '../fallbacks.js';
import { buildInfluenceBlock, OPERATION_GUARDRAILS } from '../influence.js';
import type { PromptRequest, PromptSourceBundle } from '../types.js';
import { baseRequest, type CompilerSpec } from './base.js';
import { buildCompactDialogueSections } from './contextPaths.js';
import { buildWorldContext, filterActors } from '../filters.js';

const SPEC: CompilerSpec = {
  purpose: 'runtime_scene',
  maxTier: 3,
  allowedTiers: [0, 1, 2, 3],
  budget: { inputTokens: 2500, outputTokens: 1500 },
  provider: 'deepseek',
};

export function compileRuntimeScene(source: PromptSourceBundle): PromptRequest {
  const actorIds = [
    ...new Set([...source.selectedActorIds, ...(source.nearbyActorIds ?? [])]),
  ];

  const { sections, eventIds, summaryIds } = buildCompactDialogueSections({
    source,
    actorIds,
  });

  const actors = filterActors(source.allActors, actorIds);
  const world = buildWorldContext(source);
  const influence = buildInfluenceBlock('runtime_scene', world, actors);

  const closedNote =
    source.operatingStatus === 'closed'
      ? 'LOCATION IS CLOSED — no new activity; summarize only if reviewing stored events.'
      : '';

  const header = [
    'PURPOSE: runtime_scene',
    OPERATION_GUARDRAILS.runtime_scene,
    closedNote,
    influence.text,
  ]
    .filter(Boolean)
    .join('\n');

  return baseRequest(
    source,
    SPEC,
    sections,
    actorIds,
    eventIds,
    summaryIds,
    header,
    {
      local: fallbackRuntimeScene,
      retryOnValidationError: true,
      noKeyUsesLocal: true,
    },
  );
}

export const runtimeSceneMaxTier = SPEC.maxTier;
